/**
 * « Programme de révision » de l'Aperçu : la feuille en lecture (`<TutorRevisionProgramSheet mode="read">`),
 * à la place de `StudyProgramChapterSelectionModal.js` ouverte par l'ancienne fiche (`readOnly`,
 * « Programme de révision »), et seulement pour un élève qui a un programme (`has_study_program`).
 *
 * Ce que l'ancienne modale faisait, et rien de plus — `TUTOR_STUDENT_PROGRAM_EQUIVALENCE` :
 * - à chaque ouverture, `getStudyProgramsAsTutor {} {} { student_ids: "élève" }` (`StudyProgramsFilterList`,
 *   l'id en chaîne) ; la réponse d'une lecture partie pour un autre élève n'est pas lue (`scopeUrlParams`) ;
 * - le premier programme est montré d'office, un autre se choisit ;
 * - chaque chapitre porte la progression de l'élève (`showChapterProgress`, vrai pour un tuteur et un élève) ;
 * - rien ne se presse : c'est une lecture.
 *
 * L'ancienne modale déduisait aussi un « chapitre du jour » qu'elle ne passait jamais à l'affichage
 * (`highlightedChapterId`, calculé, jamais lu) — mort, l'audit le dit. La feuille ne le reprend pas.
 *
 * La lecture et le choix du programme sont ceux de la boîte du lot 3 (`useTutorProgramPicker`) ; la fiche y
 * ajoute la progression de l'élève sur chaque chapitre.
 */
import React, { useCallback, useState } from "react"
import { useSelector } from "react-redux"

import { TutorRevisionProgramSheet } from "../../../../../../design-system/tutor"
import { type ApiSchoolSubject } from "../../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    toUITutorStudentPrograms,
    type ApiTutorStudentProgram,
} from "../../../../adapters/tutor/Students/tutorStudentProgramAdapter"
import { useTutorProgramPicker } from "../Lives/useTutorProgramPicker"
import { type TutorStudentId } from "./tutorStudentTabs"

export type TutorStudentProgramSheet = {
    /** Ouvre la feuille. Absent : pas de bouton (l'élève n'a pas de programme). */
    open: (() => void) | undefined
    overlay: React.ReactNode
}

export const TUTOR_STUDENT_PROGRAM_EQUIVALENCE = {
    open: {
        gesture: "« Programme de révision » dans l'Aperçu : la feuille, ses programmes relus",
        legacy: "common/chapters/StudyProgramsFilterList.js:38-40 (monté à chaque ouverture de la modale)",
        calls: [{ service: "getStudyProgramsAsTutor", urlParams: '{ student_ids: "élève" }' }],
    },
} as const

export function useTutorStudentProgramSheet(args: {
    studentId: TutorStudentId | null
    hasStudyProgram: boolean
}): TutorStudentProgramSheet {
    const { studentId, hasStudyProgram } = args
    const [visible, setVisible] = useState(false)

    const xpToValidate = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => (state?.data?.staticData?.xp_to_validate_chapter as number | undefined) ?? null
    )
    const adapt = useCallback(
        (raw: ApiTutorStudentProgram[] | null, schoolSubjects: ApiSchoolSubject[] | null) =>
            toUITutorStudentPrograms(raw, schoolSubjects, xpToValidate),
        [xpToValidate]
    )
    const picker = useTutorProgramPicker<ApiTutorStudentProgram>(null, adapt)
    const { load, cancel } = picker

    const open = useCallback(() => {
        if (studentId == null) return
        setVisible(true)
        // L'id en chaîne, comme l'ancien code (`StudyProgramsFilterList`).
        load(String(studentId))
    }, [load, studentId])

    const close = useCallback(() => {
        setVisible(false)
        // La feuille se ferme : une lecture encore en route n'est plus lue.
        cancel()
    }, [cancel])

    const available = hasStudyProgram && studentId != null
    return {
        open: available ? open : undefined,
        // Montée fermée dès que l'élève a un programme : la feuille garde son animation de sortie.
        overlay: available ? (
            <TutorRevisionProgramSheet
                visible={visible}
                mode="read"
                onClose={close}
                programs={picker.programs}
                selectedProgramId={picker.selectedProgramId}
                onSelectProgram={picker.onSelectProgram}
                loading={picker.loading}
                error={picker.error}
                onRetry={picker.onRetry}
            />
        ) : null,
    }
}
